import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  success: 'Успех',
  timeout: 'Таймаут',
  no_image: 'Нет картинки',
  error: 'Ошибка',
};

const STATUS_COLOR = {
  success: 'bg-emerald-100 text-emerald-700',
  timeout: 'bg-amber-100 text-amber-700',
  no_image: 'bg-orange-100 text-orange-700',
  error: 'bg-red-100 text-red-700',
};

const PERIOD_OPTIONS = [
  { key: 'week', label: 'Неделя' },
  { key: 'month', label: 'Месяц' },
  { key: 'year', label: 'Год' },
  { key: 'all', label: 'Всё время' },
  { key: 'custom', label: 'Даты…' },
];

const PERIOD_TITLE = {
  week: '7 дней',
  month: '30 дней',
  year: '365 дней',
  all: 'всё время',
  custom: 'диапазон',
};

export default function AIStatsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('week');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const load = useCallback(async (overrides = {}) => {
    setLoading(true);
    setError(null);
    try {
      const p = overrides.period || period;
      const params = new URLSearchParams({ period: p });
      if (p === 'custom') {
        const s = overrides.customStart || customStart;
        const e = overrides.customEnd || customEnd;
        if (!s || !e) {
          setError('Выберите начальную и конечную дату');
          setLoading(false);
          return;
        }
        params.set('start', s);
        params.set('end', e);
      }
      const res = await fetch(`${API_URL}/api/constructor/stats?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [token, period, customStart, customEnd]);

  // Initial + auto-reload when period changes (except custom — wait for explicit apply)
  useEffect(() => {
    if (period !== 'custom') load({ period });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const handleCustomApply = () => {
    load({ period: 'custom' });
  };

  const calls = data?.recent_calls || [];
  const periodTitle = period === 'custom' && data?.since && data?.until
    ? `${(data.since || '').slice(0, 10)} – ${(data.until || '').slice(0, 10)}`
    : PERIOD_TITLE[period];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#061d36]/10 overflow-hidden" data-testid="ai-stats-container">
      <div className="px-6 py-4 border-b border-[#061d36]/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-[#8b4444]" />
          <h2 className="text-lg font-semibold text-[#061d36]">Статистика визуализатора · {periodTitle}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg bg-[#061d36]/5 p-0.5" data-testid="ai-stats-period-tabs">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setPeriod(opt.key)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  period === opt.key ? 'bg-[#061d36] text-white' : 'text-[#061d36]/70 hover:bg-[#061d36]/10'
                }`}
                data-testid={`ai-stats-period-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => load({})}
            disabled={loading}
            className="px-3 py-1.5 text-sm bg-[#061d36] text-white rounded-lg hover:bg-[#0a2a4d] transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="ai-stats-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Обновить
          </button>
        </div>
      </div>

      {period === 'custom' && (
        <div className="px-6 py-3 border-b border-[#061d36]/10 bg-[#061d36]/5 flex flex-wrap items-center gap-3 text-sm" data-testid="ai-stats-custom-range">
          <span className="text-[#061d36]/60">Период:</span>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="px-2 py-1 border border-[#061d36]/15 rounded-md bg-white"
            data-testid="ai-stats-date-start"
          />
          <span className="text-[#061d36]/40">—</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="px-2 py-1 border border-[#061d36]/15 rounded-md bg-white"
            data-testid="ai-stats-date-end"
          />
          <button
            onClick={handleCustomApply}
            disabled={loading || !customStart || !customEnd}
            className="px-3 py-1 bg-[#8b4444] text-white rounded-md hover:bg-[#7a3838] disabled:opacity-50 transition-colors"
            data-testid="ai-stats-custom-apply"
          >
            Показать
          </button>
        </div>
      )}

      <div className="p-6 space-y-6">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
        {loading && !data && (
          <div className="flex items-center justify-center py-12 text-[#061d36]/60">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Загрузка…
          </div>
        )}
        {data && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard label="Всего запросов" value={data.total_requests} testid="kpi-total" />
              <KpiCard label="Успешных" value={data.success_count} accent="emerald" testid="kpi-success" />
              <KpiCard label="% успеха" value={`${data.success_rate_pct}%`} accent={data.success_rate_pct >= 80 ? 'emerald' : data.success_rate_pct >= 50 ? 'amber' : 'red'} testid="kpi-rate" />
              <KpiCard label="Сред. время успеха" value={`${data.avg_successful_duration_seconds}s`} testid="kpi-avg-dur" />
            </div>

            {/* Endpoint breakdown */}
            <div>
              <h3 className="text-sm font-semibold text-[#061d36]/70 mb-2">Разбивка по типу запроса</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(data.by_endpoint || {}).map(([ep, m]) => (
                  <div key={ep} className="border border-[#061d36]/10 rounded-xl p-4" data-testid={`endpoint-${ep}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-[#061d36]">
                        {ep === 'generate' ? 'С нуля (Text-to-Image)' : 'По фото (Image-to-Image)'}
                      </span>
                      <span className="text-xs text-[#061d36]/50">{m.total} запросов</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" /> {m.success_rate}%
                      </span>
                      <span className="text-[#061d36]/60">~ {m.avg_success_seconds}s</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top error categories */}
            <div>
              <h3 className="text-sm font-semibold text-[#061d36]/70 mb-2">Топ-5 категорий ошибок</h3>
              {data.top_error_categories && data.top_error_categories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {data.top_error_categories.map((c) => (
                    <span key={c.category} className={`px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_COLOR[c.category] || 'bg-gray-100 text-gray-700'}`} data-testid={`err-cat-${c.category}`}>
                      {STATUS_LABEL[c.category] || c.category} · {c.count}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#061d36]/50">Ошибок за период нет ✅</p>
              )}
            </div>

            {/* All calls (no longer limited to 15) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-[#061d36]/70">
                  Запросы за период <span className="text-[#061d36]/40 font-normal">({calls.length})</span>
                </h3>
              </div>
              <div className="border border-[#061d36]/10 rounded-xl">
                <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                  <table className="w-full text-sm">
                    <thead className="bg-[#061d36]/5 text-[#061d36]/60 text-xs uppercase sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Время</th>
                        <th className="px-3 py-2 text-left font-medium">Дилер</th>
                        <th className="px-3 py-2 text-left font-medium">Тип</th>
                        <th className="px-3 py-2 text-left font-medium">Помещение</th>
                        <th className="px-3 py-2 text-left font-medium">Текстура</th>
                        <th className="px-3 py-2 text-left font-medium">Длит.</th>
                        <th className="px-3 py-2 text-left font-medium">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.map((r, i) => (
                        <tr key={i} className="border-t border-[#061d36]/5">
                          <td className="px-3 py-2 text-[#061d36]/70 whitespace-nowrap">{(r.timestamp || '').replace('T', ' ').slice(0, 19)}</td>
                          <td className="px-3 py-2 text-[#061d36]/80">{r.user_email || '—'}</td>
                          <td className="px-3 py-2 text-[#061d36]/80">{r.endpoint}</td>
                          <td className="px-3 py-2 text-[#061d36]/80">{r.room_type || '—'}</td>
                          <td className="px-3 py-2">{r.has_texture ? '✓' : '—'}</td>
                          <td className="px-3 py-2 tabular-nums">{r.duration_seconds}s</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-700'}`}>
                              {STATUS_LABEL[r.status] || r.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {calls.length === 0 && (
                        <tr><td colSpan="7" className="px-3 py-6 text-center text-[#061d36]/50">Запросов нет</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, testid }) {
  const accentClass = accent === 'emerald' ? 'text-emerald-600' : accent === 'amber' ? 'text-amber-600' : accent === 'red' ? 'text-red-600' : 'text-[#061d36]';
  return (
    <div className="border border-[#061d36]/10 rounded-xl p-4" data-testid={testid}>
      <div className="text-xs text-[#061d36]/50 uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${accentClass}`}>{value}</div>
    </div>
  );
}
